import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Shield, ExternalLink, CheckCircle2, AlertTriangle, Loader2, Eye, Building2, FileText, ChevronDown, ChevronUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPreviewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [linkData, setLinkData] = useState(null);
  const [terms, setTerms] = useState("");
  const [error, setError] = useState("");

  // Acceptance form
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrice, setAcceptPrice] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState("");
  const [abn, setAbn] = useState("");
  const [showTerms, setShowTerms] = useState(false);

  // Browse read-only
  const [browsing, setBrowsing] = useState(false);
  const [paying, setPaying] = useState(false);

  // Stripe return-from-checkout polling
  const [polling, setPolling] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const fetchLink = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/preview/${token}`);
      setLinkData(res.data.link);
      setTerms(res.data.terms_and_conditions || "");
      // Prefill name/email if super admin set them
      if (res.data.link?.contact_name) setName(res.data.link.contact_name);
      if (res.data.link?.contact_email) setEmail(res.data.link.contact_email);
    } catch (e) {
      setError(e.response?.data?.detail || "This preview link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load + check for ?session_id from Stripe return
  useEffect(() => {
    fetchLink();
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get("session_id");
    if (sessionId) {
      pollPayment(sessionId, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pollPayment = async (sessionId, attempts) => {
    const maxAttempts = 6;
    setPolling(true);
    try {
      const res = await axios.get(`${API}/preview/checkout-status/${sessionId}`);
      const status = res.data.payment_status;
      setPaymentStatus(status);
      if (status === "paid") {
        setPolling(false);
        toast.success("Payment received — your company is now live!");
        await fetchLink();
        return;
      }
      if (res.data.status === "expired") {
        setPolling(false);
        toast.error("Stripe session expired. Please try again.");
        return;
      }
      if (attempts >= maxAttempts) {
        setPolling(false);
        toast.message("Payment is still processing. Refresh this page in a moment.");
        return;
      }
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPolling(false);
      toast.error("Could not verify payment. Please refresh.");
    }
  };

  const browseReadOnly = async () => {
    setBrowsing(true);
    try {
      const res = await axios.post(`${API}/preview/${token}/issue-readonly-token`);
      const previewToken = res.data.token;
      const companyId = res.data.company_id;
      // Store as the active session so the app uses it everywhere
      localStorage.setItem("token", previewToken);
      localStorage.setItem("currentCompanyId", companyId);
      localStorage.setItem("previewMode", "1");
      localStorage.setItem("previewReturnToken", token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${previewToken}`;
      axios.defaults.headers.common["X-Company-Id"] = companyId;
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open read-only preview");
      setBrowsing(false);
    }
  };

  const submitAcceptance = async (e) => {
    e.preventDefault();
    if (!acceptTerms || !acceptPrice) {
      toast.error("Please tick both T&Cs and price acceptance");
      return;
    }
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setPaying(true);
    try {
      const res = await axios.post(`${API}/preview/${token}/accept`, {
        accepted_terms: true,
        accepted_price: true,
        accepted_by_name: name.trim(),
        accepted_by_email: email.trim(),
        accepted_by_position: position.trim() || null,
        abn: abn.trim() || null,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Checkout URL missing");
        setPaying(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center bg-white border-2 border-red-200 rounded-md p-8" data-testid="preview-error">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <div className="text-lg font-bold text-slate-900 mb-1">Preview link unavailable</div>
          <div className="text-sm text-slate-500">{error}</div>
        </div>
      </div>
    );
  }

  const link = linkData;
  const total = (Number(link?.monthly_price_aud) || 0) + (Number(link?.setup_fee_aud) || 0);
  const isPaid = link?.status === "paid";
  const isInitiated = link?.status === "payment_initiated";

  // Stripe return-from-checkout overlay
  if (polling || paymentStatus === "paid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center bg-white border-2 border-slate-200 rounded-md p-8" data-testid="preview-payment-status">
          {paymentStatus === "paid" ? (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <div className="text-2xl font-bold text-slate-900 mb-2">Payment received</div>
              <div className="text-sm text-slate-500 mb-5">
                Your ShadowOps subscription is now active. Workers can log in using your company code <span className="font-mono font-bold">{link?.company_code}</span>.
              </div>
              <button onClick={() => navigate("/login")} className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold" data-testid="preview-go-to-login">
                Go to login
              </button>
            </>
          ) : (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-3" />
              <div className="text-lg font-bold text-slate-900 mb-1">Verifying your payment…</div>
              <div className="text-sm text-slate-500">This usually takes a few seconds.</div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (isPaid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md text-center bg-white border-2 border-emerald-200 rounded-md p-8" data-testid="preview-already-paid">
          <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <div className="text-2xl font-bold text-slate-900 mb-2">Already activated</div>
          <div className="text-sm text-slate-500 mb-5">
            This preview was accepted and paid. {link?.company_name} ({link?.company_code}) is live.
          </div>
          <button onClick={() => navigate("/login")} className="px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold">
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-orange-400 mb-2">
            <Shield className="w-4 h-4" /> Secure client preview
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading">{link.company_name}</h1>
          <p className="text-slate-300 text-sm mt-1">Code: <span className="font-mono font-bold">{link.company_code}</span></p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {link?.custom_message && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-md p-4 text-sm text-amber-900" data-testid="preview-custom-message">
            <div className="font-semibold uppercase text-xs tracking-wide text-amber-700 mb-1">A note from us</div>
            {link.custom_message}
          </div>
        )}

        {isInitiated && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-md p-4 text-sm text-blue-900">
            <div className="font-semibold mb-1">Payment in progress</div>
            You started checkout earlier. If you closed the window, you can submit again below.
          </div>
        )}

        {/* Step 1 — Browse */}
        <div className="bg-white border-2 border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">1</span>
            <h2 className="text-lg font-bold text-slate-900">Walk through your app</h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Open a read-only preview of your fully built ShadowOps profile. Everything is browseable; nothing can be changed until you accept and pay.
          </p>
          <button
            onClick={browseReadOnly}
            disabled={browsing}
            className="inline-flex items-center gap-2 px-5 py-3 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white rounded-md font-semibold"
            data-testid="preview-browse-btn"
          >
            <Eye className="w-4 h-4" />
            {browsing ? "Opening…" : "Open read-only preview"}
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>

        {/* Step 2 — Price */}
        <div className="bg-white border-2 border-slate-200 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">2</span>
            <h2 className="text-lg font-bold text-slate-900">Your subscription</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div className="border-2 border-slate-200 rounded p-3">
              <div className="text-xs uppercase text-slate-500 font-semibold">Setup fee</div>
              <div className="text-xl font-bold text-slate-900">${Number(link.setup_fee_aud || 0).toFixed(2)}</div>
            </div>
            <div className="border-2 border-slate-200 rounded p-3">
              <div className="text-xs uppercase text-slate-500 font-semibold">First month</div>
              <div className="text-xl font-bold text-slate-900">${Number(link.monthly_price_aud || 0).toFixed(2)}</div>
            </div>
            <div className="border-2 border-orange-300 bg-orange-50 rounded p-3">
              <div className="text-xs uppercase text-orange-700 font-semibold">Pay today</div>
              <div className="text-xl font-bold text-slate-900" data-testid="preview-total-aud">${total.toFixed(2)} AUD</div>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Prices exclusive of GST. Subsequent months auto-renew at ${Number(link.monthly_price_aud || 0).toFixed(2)}/mo until you cancel in writing (30-day notice).
          </p>
        </div>

        {/* Step 3 — T&C + Acceptance */}
        <form onSubmit={submitAcceptance} className="bg-white border-2 border-slate-200 rounded-md p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">3</span>
            <h2 className="text-lg font-bold text-slate-900">Accept & pay</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Your name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required
                className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
                data-testid="preview-input-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
                data-testid="preview-input-email" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Position</label>
              <input value={position} onChange={(e) => setPosition(e.target.value)}
                placeholder="e.g. Director"
                className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
                data-testid="preview-input-position" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">ABN (optional)</label>
              <input value={abn} onChange={(e) => setAbn(e.target.value)}
                placeholder="e.g. 12 345 678 901"
                className="w-full border-2 border-slate-200 rounded-md px-3 py-2"
                data-testid="preview-input-abn" />
            </div>
          </div>

          {/* T&C collapsible */}
          <div className="border-2 border-slate-200 rounded-md">
            <button type="button" onClick={() => setShowTerms(s => !s)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="preview-toggle-terms">
              <span className="flex items-center gap-2"><FileText className="w-4 h-4" /> Read Terms & Conditions</span>
              {showTerms ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showTerms && (
              <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap px-4 py-3 text-xs text-slate-700 border-t border-slate-200 font-sans" data-testid="preview-terms-text">
                {terms}
              </pre>
            )}
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
              className="w-5 h-5 mt-0.5" data-testid="preview-check-terms" />
            <span className="text-sm text-slate-700">
              I have read and agree to the <span className="font-semibold">ShadowOps Terms & Conditions</span> above and confirm I have authority to bind my organisation.
            </span>
          </label>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={acceptPrice} onChange={(e) => setAcceptPrice(e.target.checked)}
              className="w-5 h-5 mt-0.5" data-testid="preview-check-price" />
            <span className="text-sm text-slate-700">
              I agree to pay <span className="font-semibold">${total.toFixed(2)} AUD</span> today and the recurring monthly subscription as detailed above.
            </span>
          </label>

          <button type="submit" disabled={paying || !acceptTerms || !acceptPrice}
            className="w-full px-5 py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-md font-bold text-base inline-flex items-center justify-center gap-2"
            data-testid="preview-submit-checkout">
            {paying ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Pay ${total.toFixed(2)} AUD via Stripe</>}
          </button>

          <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
            <Building2 className="w-3 h-3" /> Powered by Stripe · Card details never touch our servers.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ClientPreviewPage;
